// D24 archive species — the ONE decision that tells a manually-archived chat
// (writable, returns on a new message) apart from a timer-EXPIRED one
// (read-only, permanent, revive-only). Both live behind Stream's per-member
// `archived` flag, so this classifier reads the channel's timer stamp to split
// them. Kept pure + standalone so the row UI, the archive section and the
// auto-return handler all share exactly one rule (and it is unit-testable).

export const ARCHIVE_SPECIES = {
  MANUAL: "manual", // archived by the viewer; writable; returns on new message
  EXPIRED: "expired", // timer expired; read-only; permanent (revive only)
};

// The backend stamps `timer_status:"expired"` on the channel the moment a
// club↔agent (or timed agent↔agent) chat's timer runs out; a manual archive
// never touches that field. So an archived channel is EXPIRED iff that stamp is
// present, else MANUAL. Accepts a channel or a raw channel.data object.
export const isExpiredChannel = (channelOrData) => {
  const data = channelOrData?.data || channelOrData || {};
  const ctx = data._tp_custom_context || {};
  return (
    data.timer_status === "expired" || ctx.timer_status === "expired"
  );
};

export const archiveSpecies = (channelOrData) =>
  isExpiredChannel(channelOrData)
    ? ARCHIVE_SPECIES.EXPIRED
    : ARCHIVE_SPECIES.MANUAL;

// AUTO-RETURN (D24c, client side): a `message.new` on a chat I manually
// archived pulls it back into the main list. This predicate decides whether a
// given incoming event should trigger that return. It fires only for a chat the
// viewer archived MANUALLY (never an expired one — those stay read-only) and
// skips system/ephemeral messages and the viewer's own sends (a message you
// send from the archived thread already reopens it locally). The durable path
// is the backend `message.new` webhook; this is the live-session mirror so the
// row moves without a reload.
//
// @param {object} args
// @param {object} args.channel   the Stream channel the event is for
// @param {object} args.message   the event's message
// @param {string} args.selfId    the viewer's Stream user id
// @param {(cid:string)=>boolean} args.isManuallyArchived  did I archive this cid?
export const shouldAutoReturn = ({ channel, message, selfId, isManuallyArchived }) => {
  if (!channel || !message) return false;
  // Expired chats never return automatically — they are permanent read-only.
  if (isExpiredChannel(channel)) return false;
  const cid = String(channel.cid || channel.id || "");
  if (!cid || !isManuallyArchived(cid)) return false;
  // Skip system / ephemeral messages (membership events, timer notices, …) and
  // the viewer's own message (that path reopens the thread locally already).
  const type = message.type;
  if (type === "system" || type === "ephemeral" || type === "deleted") return false;
  const senderId = String(message.user?.id ?? "");
  if (senderId && selfId && senderId === String(selfId)) return false;
  return true;
};
